import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type AlertType = 'stage' | 'capability';

export interface HealthAlert {
  id: string;
  type: AlertType;
  title: string;
  nodeName: string;
  health: number;
  timestamp: Date;
}

export interface GraphSearchAPI {
  search: (term: string) => void;
  zoomStageHeader: (stageName: string) => void;
}

interface AlertsContextValue {
  alerts: HealthAlert[];
  addAlert: (alert: Omit<HealthAlert, 'id' | 'timestamp'>) => void;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;
  isAlertsPanelOpen: boolean;
  setAlertsPanelOpen: (open: boolean) => void;
  toggleAlertsPanel: () => void;
  searchApi: GraphSearchAPI | null;
  setSearchApi: (api: GraphSearchAPI | null) => void;
}

const AlertsContext = createContext<AlertsContextValue | undefined>(undefined);

let alertIdCounter = 0;

function generateAlertId(): string {
  alertIdCounter = (alertIdCounter + 1) % Number.MAX_SAFE_INTEGER;
  return `alert-${alertIdCounter}-${Date.now()}`;
}

export function AlertsProvider({ children }: { children: ReactNode }) {
  const [alerts, setAlerts] = useState<HealthAlert[]>([]);
  const [isAlertsPanelOpen, setAlertsPanelOpen] = useState(false);
  const [searchApi, setSearchApi] = useState<GraphSearchAPI | null>(null);

  const addAlert = useCallback((alert: Omit<HealthAlert, 'id' | 'timestamp'>) => {
    const newAlert: HealthAlert = {
      ...alert,
      id: generateAlertId(),
      timestamp: new Date(),
    };
    setAlerts((prev) => [newAlert, ...prev]);
  }, []);

  const removeAlert = useCallback((id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  const toggleAlertsPanel = useCallback(() => {
    setAlertsPanelOpen((prev) => !prev);
  }, []);

  return (
    <AlertsContext.Provider
      value={{
        alerts,
        addAlert,
        removeAlert,
        clearAlerts,
        isAlertsPanelOpen,
        setAlertsPanelOpen,
        toggleAlertsPanel,
        searchApi,
        setSearchApi,
      }}
    >
      {children}
    </AlertsContext.Provider>
  );
}

export function useAlerts(): AlertsContextValue {
  const context = useContext(AlertsContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertsProvider');
  }
  return context;
}
